import React, { useState, useEffect, useRef } from 'react';
import { Info, Pin, PinOff, Edit2, Maximize2, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { Modal } from '../admin/Modal';

// ============================================================================
// TYPES
// ============================================================================

interface ImportantInfo {
  content: string;
  last_updated: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
}

interface PinnedNote {
  id: string;
  title: string;
  preview: string;  // Truncated preview for card display
  note_type: string;
  session_date: string;
  created_at: string;
  created_by_name: string;
  signed_at: string | null;
  tags: string[];
  sections: {
    subjective?: { content: string; client_visible: boolean };
    objective?: { coach_observations: string; client_visible: boolean };
    assessment?: { content: string; client_visible: boolean };
    plan?: { content: string; client_visible: boolean };
  };
}

interface ClientNotesPanelProps {
  clientId: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ClientNotesPanel - Tabbed control for Important Info + Pinned Notes
 *
 * Tab 1: Important Info
 *   - Static high-value context (allergies, key metrics, concerns)
 *   - Coach-editable, markdown-supported
 *   - Edit / Pop-out / Expand buttons
 *
 * Tab 2: Pinned Notes
 *   - List of session notes that have been "pinned"
 *   - Each shows title/date, 3-4 line preview
 *   - Expand (inline) / Pop-out (modal) for each note
 *   - Unpin option to remove from list
 */
export const ClientNotesPanel: React.FC<ClientNotesPanelProps> = ({ clientId }) => {
  const [activeTab, setActiveTab] = useState<'important' | 'pinned'>('important');

  // Important Info state
  const [importantInfo, setImportantInfo] = useState<ImportantInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [infoExpanded, setInfoExpanded] = useState(false);
  const [showInfoPopout, setShowInfoPopout] = useState(false);
  const [showInfoEdit, setShowInfoEdit] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  // Pinned Notes state
  const [pinnedNotes, setPinnedNotes] = useState<PinnedNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [popoutNote, setPopoutNote] = useState<PinnedNote | null>(null);
  const [hoveredPinId, setHoveredPinId] = useState<string | null>(null);

  // Drag and drop state
  const [draggedNoteId, setDraggedNoteId] = useState<string | null>(null);
  const [dragOverNoteId, setDragOverNoteId] = useState<string | null>(null);
  const dragCounter = useRef(0);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    loadImportantInfo();
    loadPinnedNotes();
  }, [clientId]);

  const loadImportantInfo = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/pinned-notes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setImportantInfo(data.pinnedNotes);
      }
    } catch (error) {
      console.error('Error loading important info:', error);
    } finally {
      setLoadingInfo(false);
    }
  };

  const loadPinnedNotes = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/notes/pinned`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPinnedNotes(data.pinnedNotes || []);
      }
    } catch (error) {
      console.error('Error loading pinned notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  };

  // ============================================================================
  // IMPORTANT INFO HANDLERS
  // ============================================================================

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/pinned-notes`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ content: editContent })
      });

      if (response.ok) {
        const data = await response.json();
        setImportantInfo(data.pinnedNotes);
        setShowInfoEdit(false);
      } else {
        const error = await response.json();
        alert(`Failed to save: ${error.error}`);
      }
    } catch (error) {
      console.error('Error saving important info:', error);
      alert('Failed to save important info');
    } finally {
      setSaving(false);
    }
  };

  const openInfoEdit = () => {
    setEditContent(importantInfo?.content || '');
    setShowInfoEdit(true);
  };

  // ============================================================================
  // PINNED NOTES HANDLERS
  // ============================================================================

  const toggleNoteExpanded = (noteId: string) => {
    setExpandedNotes(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) {
        next.delete(noteId);
      } else {
        next.add(noteId);
      }
      return next;
    });
  };

  const handleUnpinNote = async (noteId: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/coach/clients/${clientId}/notes/${noteId}/unpin`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setPinnedNotes(prev => prev.filter(n => n.id !== noteId));
      }
    } catch (error) {
      console.error('Error unpinning note:', error);
    }
  };

  // ============================================================================
  // DRAG AND DROP HANDLERS
  // ============================================================================

  const handleDragStart = (e: React.DragEvent, noteId: string) => {
    setDraggedNoteId(noteId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', noteId);

    // Set the drag image to the whole card, not just the handle
    const card = document.querySelector(`[data-note-id="${noteId}"]`) as HTMLElement;
    if (card) {
      // Position the drag image at the cursor position relative to the card
      const rect = card.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      e.dataTransfer.setDragImage(card, offsetX, offsetY);

      // Add dragging class after a brief delay
      setTimeout(() => card.classList.add('dragging'), 0);
    }
  };

  const handleDragEnd = () => {
    setDraggedNoteId(null);
    setDragOverNoteId(null);
    dragCounter.current = 0;
    // Remove dragging class from all elements
    document.querySelectorAll('.dragging').forEach(el => el.classList.remove('dragging'));
  };

  const handleDragEnter = (e: React.DragEvent, noteId: string) => {
    e.preventDefault();
    dragCounter.current++;
    if (noteId !== draggedNoteId) {
      setDragOverNoteId(noteId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setDragOverNoteId(null);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetNoteId: string) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOverNoteId(null);

    if (!draggedNoteId || draggedNoteId === targetNoteId) {
      setDraggedNoteId(null);
      return;
    }

    // Reorder the notes array
    const newNotes = [...pinnedNotes];
    const draggedIndex = newNotes.findIndex(n => n.id === draggedNoteId);
    const targetIndex = newNotes.findIndex(n => n.id === targetNoteId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedNoteId(null);
      return;
    }

    // Remove dragged item and insert at target position
    const [draggedNote] = newNotes.splice(draggedIndex, 1);
    newNotes.splice(targetIndex, 0, draggedNote);

    // Update local state immediately for responsiveness
    setPinnedNotes(newNotes);
    setDraggedNoteId(null);

    // Save the new order to backend
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`/api/coach/clients/${clientId}/notes/pinned/order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ order: newNotes.map(n => n.id) })
      });
    } catch (error) {
      console.error('Error saving note order:', error);
      // Could reload notes here to revert on error, but optimistic update is fine for UX
    }
  };

  // ============================================================================
  // MARKDOWN RENDERER
  // ============================================================================

  const renderMarkdown = (text: string) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let inList = false;
    let listItems: string[] = [];

    const processInlineFormatting = (line: string): React.ReactNode => {
      let remaining = line;
      remaining = remaining.replace(/\*\*(.+?)\*\*|__(.+?)__/g, (_, g1, g2) => `<b>${g1 || g2}</b>`);
      remaining = remaining.replace(/(?<!<b>)\*(.+?)\*(?!<\/b>)|(?<!<b>)_(.+?)_(?!<\/b>)/g,
        (_, g1, g2) => `<i>${g1 || g2}</i>`);

      const htmlPattern = /<(b|i)>(.+?)<\/\1>/g;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;
      let key = 0;

      while ((match = htmlPattern.exec(remaining)) !== null) {
        if (match.index > lastIndex) {
          parts.push(remaining.slice(lastIndex, match.index));
        }
        if (match[1] === 'b') {
          parts.push(<strong key={key++}>{match[2]}</strong>);
        } else {
          parts.push(<em key={key++}>{match[2]}</em>);
        }
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < remaining.length) {
        parts.push(remaining.slice(lastIndex));
      }

      return parts.length > 0 ? parts : remaining;
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <ul key={elements.length} style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {listItems.map((item, i) => (
              <li key={i} style={{ marginBottom: '4px' }}>{processInlineFormatting(item)}</li>
            ))}
          </ul>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line, index) => {
      const bulletMatch = line.match(/^[\s]*[-*]\s+(.+)/);
      if (bulletMatch) {
        inList = true;
        listItems.push(bulletMatch[1]);
        return;
      } else if (inList) {
        flushList();
      }

      if (line.startsWith('### ')) {
        elements.push(
          <h4 key={index} style={{ fontSize: '14px', fontWeight: 600, margin: '12px 0 8px' }}>
            {processInlineFormatting(line.slice(4))}
          </h4>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h3 key={index} style={{ fontSize: '15px', fontWeight: 600, margin: '12px 0 8px' }}>
            {processInlineFormatting(line.slice(3))}
          </h3>
        );
      } else if (line.startsWith('# ')) {
        elements.push(
          <h2 key={index} style={{ fontSize: '16px', fontWeight: 600, margin: '12px 0 8px' }}>
            {processInlineFormatting(line.slice(2))}
          </h2>
        );
      } else if (line.trim() === '') {
        elements.push(<div key={index} style={{ height: '8px' }} />);
      } else {
        elements.push(
          <p key={index} style={{ margin: '4px 0', lineHeight: 1.5 }}>
            {processInlineFormatting(line)}
          </p>
        );
      }
    });

    flushList();
    return elements;
  };

  const getTruncatedContent = (content: string, lines: number = 5) => {
    if (!content) return { content: '', hasMore: false };
    const contentLines = content.split('\n').slice(0, lines);
    const truncated = contentLines.join('\n');
    const hasMore = content.split('\n').length > lines;
    return { content: truncated, hasMore };
  };

  const buildFullContent = (sections: PinnedNote['sections']) => {
    const parts: string[] = [];

    if (sections?.subjective?.content) {
      parts.push('## Subjective');
      parts.push(sections.subjective.content);
    }
    if (sections?.objective?.coach_observations) {
      parts.push('## Observations');
      parts.push(sections.objective.coach_observations);
    }
    if (sections?.assessment?.content) {
      parts.push('## Assessment');
      parts.push(sections.assessment.content);
    }
    if (sections?.plan?.content) {
      parts.push('## Plan');
      parts.push(sections.plan.content);
    }

    return parts.join('\n\n');
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  const hasImportantInfo = importantInfo?.content && importantInfo.content.trim().length > 0;
  const truncatedInfo = getTruncatedContent(importantInfo?.content || '', 5);

  return (
    <>
      <div className="card client-notes-panel">
        {/* Tab Headers */}
        <div className="tabs-header">
          <button
            className={`tab-btn ${activeTab === 'important' ? 'active' : ''}`}
            onClick={() => setActiveTab('important')}
          >
            <Info size={14} />
            Important Info
          </button>
          <button
            className={`tab-btn ${activeTab === 'pinned' ? 'active' : ''}`}
            onClick={() => setActiveTab('pinned')}
          >
            <Pin size={14} />
            Pinned Notes
            {pinnedNotes.length > 0 && (
              <span className="tab-badge">{pinnedNotes.length}</span>
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* ============ IMPORTANT INFO TAB ============ */}
          {activeTab === 'important' && (
            <div className="important-info-tab">
              <div className="tab-actions">
                <button className="icon-btn" onClick={openInfoEdit} title="Edit">
                  <Edit2 size={14} />
                </button>
                {hasImportantInfo && (
                  <>
                    <button className="icon-btn" onClick={() => setShowInfoPopout(true)} title="Pop-out">
                      <Maximize2 size={14} />
                    </button>
                    <button className="expand-btn" onClick={() => setInfoExpanded(!infoExpanded)}>
                      {infoExpanded ? <>Collapse <ChevronUp size={14} /></> : <>Expand <ChevronDown size={14} /></>}
                    </button>
                  </>
                )}
              </div>

              {loadingInfo ? (
                <div className="placeholder-content"><p>Loading...</p></div>
              ) : hasImportantInfo ? (
                <div className="info-content">
                  <div className="markdown-content">
                    {infoExpanded
                      ? renderMarkdown(importantInfo!.content)
                      : renderMarkdown(truncatedInfo.content)
                    }
                  </div>
                  {!infoExpanded && truncatedInfo.hasMore && (
                    <div className="truncated-indicator">...</div>
                  )}
                  {importantInfo?.last_updated && (
                    <div className="content-meta">
                      Updated by {importantInfo.updated_by_name} on{' '}
                      {new Date(importantInfo.last_updated).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="placeholder-content">
                  <p>No important info yet</p>
                  <p className="hint">Click Edit to add critical client information</p>
                </div>
              )}
            </div>
          )}

          {/* ============ PINNED NOTES TAB ============ */}
          {activeTab === 'pinned' && (
            <div className="pinned-notes-tab">
              {loadingNotes ? (
                <div className="placeholder-content"><p>Loading...</p></div>
              ) : pinnedNotes.length > 0 ? (
                <div className="pinned-notes-list">
                  {pinnedNotes.map(note => {
                    const isExpanded = expandedNotes.has(note.id);
                    const truncated = getTruncatedContent(note.preview, 4);
                    const isDragOver = dragOverNoteId === note.id;

                    return (
                      <div
                        key={note.id}
                        data-note-id={note.id}
                        className={`pinned-note-card ${isDragOver ? 'drag-over' : ''}`}
                        onDragEnter={(e) => handleDragEnter(e, note.id)}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, note.id)}
                      >
                        <div className="note-header">
                          <div className="note-title-row">
                            <div
                              className="drag-handle"
                              title="Drag to reorder"
                              draggable
                              onDragStart={(e) => handleDragStart(e, note.id)}
                              onDragEnd={handleDragEnd}
                            >
                              <GripVertical size={14} />
                            </div>
                            <div className="note-title-info">
                              <span className="note-title">{note.title}</span>
                              <span className="note-date">
                                {new Date(note.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="note-actions">
                            <button
                              className="icon-btn"
                              onClick={() => setPopoutNote(note)}
                              title="Pop-out"
                            >
                              <Maximize2 size={14} />
                            </button>
                            <button
                              className="icon-btn pin-btn pinned"
                              onClick={() => handleUnpinNote(note.id)}
                              onMouseEnter={() => setHoveredPinId(note.id)}
                              onMouseLeave={() => setHoveredPinId(null)}
                              title="Unpin"
                            >
                              {/* Show PinOff when hovering (about to unpin), Pin otherwise */}
                              {hoveredPinId === note.id ? <PinOff size={14} /> : <Pin size={14} />}
                            </button>
                            <button
                              className="expand-btn"
                              onClick={() => toggleNoteExpanded(note.id)}
                            >
                              {isExpanded ? <>Collapse <ChevronUp size={14} /></> : <>Expand <ChevronDown size={14} /></>}
                            </button>
                          </div>
                        </div>
                        <div className="note-preview">
                          <div className="markdown-content">
                            {isExpanded
                              ? renderMarkdown(buildFullContent(note.sections))
                              : renderMarkdown(truncated.content)
                            }
                          </div>
                          {!isExpanded && truncated.hasMore && (
                            <div className="truncated-indicator">...</div>
                          )}
                        </div>
                        <div className="note-meta">
                          {note.session_date} &middot; By {note.created_by_name}
                          {note.tags.length > 0 && (
                            <span className="note-tags">
                              {note.tags.map(tag => (
                                <span key={tag} className="note-tag">{tag}</span>
                              ))}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="placeholder-content">
                  <p>No pinned notes</p>
                  <p className="hint">Pin important session notes for quick access</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ============ MODALS ============ */}

      {/* Important Info Pop-out Modal */}
      <Modal
        isOpen={showInfoPopout}
        onClose={() => setShowInfoPopout(false)}
        title="Important Info"
        variant="info"
        size="medium"
        footer={
          <button className="btn btn-secondary" onClick={() => setShowInfoPopout(false)}>
            Close
          </button>
        }
      >
        <div className="markdown-content popout-content">
          {renderMarkdown(importantInfo?.content || '')}
        </div>
        {importantInfo?.last_updated && (
          <div className="content-meta" style={{ marginTop: '16px' }}>
            Updated by {importantInfo.updated_by_name} on{' '}
            {new Date(importantInfo.last_updated).toLocaleDateString()}
          </div>
        )}
      </Modal>

      {/* Important Info Edit Modal */}
      <Modal
        isOpen={showInfoEdit}
        onClose={() => setShowInfoEdit(false)}
        title="Edit Important Info"
        variant="info"
        size="large"
        hasUnsavedChanges={editContent !== (importantInfo?.content || '')}
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowInfoEdit(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveInfo} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </>
        }
      >
        <div className="edit-info-container">
          <div className="formatting-toolbar">
            <button className="toolbar-btn" onClick={() => setEditContent(prev => prev + '**bold**')} title="Bold">
              <strong>B</strong>
            </button>
            <button className="toolbar-btn" onClick={() => setEditContent(prev => prev + '*italic*')} title="Italic">
              <em>I</em>
            </button>
            <button className="toolbar-btn" onClick={() => setEditContent(prev => prev + '\n- ')} title="Bullet list">
              &bull;
            </button>
            <button className="toolbar-btn" onClick={() => setEditContent(prev => prev + '\n## ')} title="Heading">
              H
            </button>
          </div>
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            placeholder="Enter critical client information here...

Use markdown formatting:
- **bold** for emphasis
- *italic* for secondary emphasis
- Bullet lists with - or *
- ## Headers for sections

Examples:
## Allergies
- Penicillin
- Shellfish

## Key Metrics to Monitor
- Blood pressure (high readings last 3 visits)
- **A1C levels** - needs improvement"
            className="info-textarea"
            rows={15}
          />
          <div className="preview-section">
            <h4>Preview</h4>
            <div className="markdown-content preview-content">
              {renderMarkdown(editContent)}
            </div>
          </div>
        </div>
      </Modal>

      {/* Pinned Note Pop-out Modal */}
      <Modal
        isOpen={!!popoutNote}
        onClose={() => setPopoutNote(null)}
        title={popoutNote?.title || 'Note'}
        variant="info"
        size="medium"
        footer={
          <button className="btn btn-secondary" onClick={() => setPopoutNote(null)}>
            Close
          </button>
        }
      >
        {popoutNote && (
          <>
            <div className="note-modal-meta">
              <span className="note-type-badge">{popoutNote.note_type}</span>
              <span className="note-date">{popoutNote.session_date}</span>
              {popoutNote.tags.map(tag => (
                <span key={tag} className="note-tag">{tag}</span>
              ))}
            </div>
            <div className="markdown-content popout-content">
              {renderMarkdown(buildFullContent(popoutNote.sections))}
            </div>
            <div className="content-meta" style={{ marginTop: '16px' }}>
              By {popoutNote.created_by_name}
              {popoutNote.signed_at && ` • Signed ${new Date(popoutNote.signed_at).toLocaleDateString()}`}
            </div>
          </>
        )}
      </Modal>

      <style>{`
        .client-notes-panel {
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        /* Tab Headers */
        .tabs-header {
          display: flex;
          border-bottom: 1px solid var(--border-secondary);
          padding: 0 16px;
          gap: 4px;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 12px 16px;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-secondary);
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          margin-bottom: -1px;
        }

        .tab-btn:hover {
          color: var(--text-primary);
          background: var(--bg-primary);
        }

        .tab-btn.active {
          color: var(--accent-green);
          border-bottom-color: var(--accent-green);
        }

        .tab-btn svg {
          opacity: 0.7;
        }

        .tab-btn.active svg {
          opacity: 1;
        }

        .tab-badge {
          background: var(--accent-green);
          color: white;
          font-size: 11px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
        }

        /* Tab Content */
        .tab-content {
          padding: 16px;
          flex: 1;
          overflow-y: auto;
        }

        .tab-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          justify-content: flex-end;
        }

        .icon-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: transparent;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s;
        }

        .icon-btn:hover {
          background: var(--bg-primary);
          border-color: var(--accent-blue);
          color: var(--accent-blue);
        }

        /* Pin button - pinned state (green background, white icon) */
        .icon-btn.pin-btn.pinned {
          color: white;
          border-color: white;
          background: var(--accent-green);
        }

        /* Pin button - hover on pinned (red background, ready to unpin) */
        .icon-btn.pin-btn.pinned:hover {
          color: white;
          border-color: white;
          background: var(--accent-red, #e53e3e);
        }

        .expand-btn {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 12px;
          color: var(--text-secondary);
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: var(--radius-small);
        }

        .expand-btn:hover {
          color: var(--accent-green);
          background: var(--bg-primary);
        }

        .inline-expand {
          margin-top: 8px;
        }

        /* Content Styles */
        .info-content,
        .pinned-note-card {
          background: var(--bg-primary);
          border-radius: var(--radius-medium);
          padding: 16px;
          border: 1px solid var(--border-secondary);
        }

        .pinned-notes-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .pinned-note-card {
          padding: 12px;
          transition: all 0.15s;
        }

        .pinned-note-card.dragging {
          opacity: 0.5;
          transform: scale(0.98);
        }

        .pinned-note-card.drag-over {
          border-color: var(--accent-green);
          background: var(--accent-green-bg);
          transform: translateY(2px);
        }

        .drag-handle {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-tertiary);
          cursor: grab;
          padding: 2px;
          margin-right: 8px;
          border-radius: var(--radius-small);
          transition: all 0.15s;
        }

        .drag-handle:hover {
          color: var(--text-secondary);
          background: var(--bg-secondary);
        }

        .drag-handle:active {
          cursor: grabbing;
        }

        .note-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }

        .note-title-row {
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          gap: 0;
        }

        .note-title-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .note-title {
          font-weight: 600;
          font-size: 14px;
          color: var(--text-primary);
        }

        .note-date {
          font-size: 11px;
          color: var(--text-tertiary);
        }

        .note-actions {
          display: flex;
          gap: 8px;
        }

        .note-preview {
          font-size: 13px;
        }

        .markdown-content {
          color: var(--text-primary);
          font-size: 14px;
          line-height: 1.6;
        }

        .markdown-content strong {
          font-weight: 600;
          color: var(--accent-blue);
        }

        .truncated-indicator {
          text-align: center;
          color: var(--text-tertiary);
          font-size: 16px;
          letter-spacing: 2px;
          margin-top: 4px;
        }

        .content-meta,
        .note-meta {
          font-size: 11px;
          color: var(--text-tertiary);
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid var(--border-secondary);
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }

        .note-tags {
          display: flex;
          gap: 4px;
          margin-left: 8px;
        }

        .note-tag {
          background: var(--bg-secondary);
          color: var(--text-secondary);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
        }

        .placeholder-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
          text-align: center;
          color: var(--text-secondary);
          background: var(--bg-primary);
          border-radius: var(--radius-medium);
          border: 2px dashed var(--border-secondary);
        }

        .placeholder-content p {
          margin: 0;
          font-size: 14px;
        }

        .placeholder-content .hint {
          font-size: 12px;
          margin-top: 4px;
          opacity: 0.7;
        }

        /* Pop-out Modal */
        .popout-content {
          max-height: 60vh;
          overflow-y: auto;
        }

        .note-modal-meta {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border-secondary);
        }

        .note-type-badge {
          background: var(--accent-blue-bg);
          color: var(--accent-blue);
          padding: 4px 8px;
          border-radius: var(--radius-small);
          font-size: 12px;
          font-weight: 500;
        }

        /* Edit Modal */
        .edit-info-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .formatting-toolbar {
          display: flex;
          gap: 8px;
          padding: 8px;
          background: var(--bg-secondary);
          border-radius: var(--radius-small);
        }

        .toolbar-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          cursor: pointer;
          font-size: 14px;
          color: var(--text-primary);
          transition: all 0.15s;
        }

        .toolbar-btn:hover {
          background: var(--accent-blue-bg);
          border-color: var(--accent-blue);
          color: var(--accent-blue);
        }

        .info-textarea {
          width: 100%;
          padding: 16px;
          font-family: var(--font-system);
          font-size: 14px;
          line-height: 1.6;
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          background: var(--bg-primary);
          color: var(--text-primary);
          resize: vertical;
          min-height: 200px;
        }

        .info-textarea:focus {
          outline: none;
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px var(--accent-blue-bg);
        }

        .info-textarea::placeholder {
          color: var(--text-tertiary);
        }

        .preview-section {
          border-top: 1px solid var(--border-secondary);
          padding-top: 16px;
        }

        .preview-section h4 {
          font-size: 13px;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .preview-content {
          background: var(--bg-secondary);
          padding: 16px;
          border-radius: var(--radius-medium);
          min-height: 100px;
          max-height: 200px;
          overflow-y: auto;
        }
      `}</style>
    </>
  );
};

export default ClientNotesPanel;
